// ═══════════════════════════════════════════════════════════════
// Intake — Register New Individual
// Method selection (Scan DL, AI Scanner, Manual) + 3-step stepper form
// Ported from Intake.jsx + IntakeForm.jsx + TriageSelector.jsx +
//   AIScanner.jsx + BarcodeScanner.jsx
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Textarea, Select, Button,
  Group, Stack, Stepper, SimpleGrid, Image, FileButton, Badge,
  Center, ThemeIcon, Divider, ActionIcon, CopyButton, Tooltip,
  Modal, Checkbox, Loader,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconUserPlus, IconArrowLeft, IconArrowRight, IconCheck,
  IconCamera, IconTrash, IconRefresh, IconCopy, IconPhoto,
  IconScan, IconRobot, IconEdit, IconX, IconPrinter,
} from '@tabler/icons-react';
import { useIntakeStore } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import { generateTagCode, calcAge, parseAAMVA, resizeImageToBase64 } from './lib/helpers';
import { formatPhone } from '../../lib/phoneFormat';
import { printBadge } from './Roster';
import type { Individual } from '../../store/intakeStore';
import { WORKER_URL } from '../../lib/api-switch';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

// ── Constants ─────────────────────────────────────────────────
const ORIGIN = 'https://apps.opengov.com';

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'X', label: 'Non-Binary / Other' },
];

const ID_TYPE_OPTIONS = [
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'state_id', label: 'State ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'military', label: 'Military ID' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'No ID' },
];

type IntakeMethod = 'scan' | 'ai' | 'manual' | null;

// ── Form state ────────────────────────────────────────────────

interface FormState {
  firstName: string;
  lastName: string;
  middleName: string;
  dob: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  idType: string;
  isMinor: boolean;
  guardianId: string;
  triage: string;
  medicalNeeds: string;
  medications: string;
  emergencyContact: string;
  contactPhone: string;
  notes: string;
  tagCode: string;
  photo: File | null;
  photoPreview: string;
}

const INITIAL_FORM: FormState = {
  firstName: '', lastName: '', middleName: '',
  dob: '', gender: '', phone: '', email: '',
  address: '', city: '', state: '', zip: '',
  idType: '', isMinor: false, guardianId: '',
  triage: 'var(--color-success)',
  medicalNeeds: '', medications: '',
  emergencyContact: '', contactPhone: '',
  notes: '',
  tagCode: generateTagCode(),
  photo: null, photoPreview: '',
};

// ══════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════

export default function Register() {
  const navigate = useNavigate();
  const { createIndividual, individuals } = useIntakeStore();
  const { activeIncident } = useAuthStore();

  const [method, setMethod] = useState<IntakeMethod>(null);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);

  // Directory person lookup (Phase 6)
  const [linkedPersonId, setLinkedPersonId] = useState<number | null>(null);
  const [dirQuery, setDirQuery] = useState('');
  const [dirResults, setDirResults] = useState<Array<{ record_id: number; firstName: string; lastName: string; phone: string }>>([]);
  const [dirSearching, setDirSearching] = useState(false);
  const dirDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleDirQuery(q: string) {
    setDirQuery(q);
    if (dirDebounce.current) clearTimeout(dirDebounce.current);
    if (!q.trim() || !TABLES.people) { setDirResults([]); return; }
    dirDebounce.current = setTimeout(async () => {
      setDirSearching(true);
      try {
        const r = await queryRecords(TABLES.people, { search: q.trim(), limit: 6 });
        setDirResults(r.records.map((row: Record<string, unknown>) => ({
          record_id: row.record_id as number,
          firstName:  (row[FIELDS.people.firstName]  ?? '') as string,
          lastName:   (row[FIELDS.people.lastName]   ?? '') as string,
          phone:      (row[FIELDS.people.phone]      ?? '') as string,
        })));
      } catch { /* ignore */ } finally { setDirSearching(false); }
    }, 300);
  }

  function selectDirPerson(p: typeof dirResults[0]) {
    setLinkedPersonId(p.record_id);
    updateForm('firstName', p.firstName || form.firstName);
    updateForm('lastName',  p.lastName  || form.lastName);
    if (p.phone) updateForm('contactPhone', p.phone);
    setDirQuery(`${p.firstName} ${p.lastName}`);
    setDirResults([]);
    notifications.show({ message: `Linked to directory: ${p.firstName} ${p.lastName}`, color: 'var(--color-success)' });
  }
  const [successData, setSuccessData] = useState<{ name: string; tagCode: string; individual: Partial<Individual> } | null>(null);

  // Barcode scanner state
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanHint, setScanHint] = useState('Point at the barcode on the back of the ID');
  const [scanError, setScanError] = useState('');
  const scanVideoRef = useRef<HTMLVideoElement>(null);
  const scanCanvasRef = useRef<HTMLCanvasElement>(null);
  const scanStreamRef = useRef<MediaStream | null>(null);
  const scanActiveRef = useRef(false);
  const scanRafRef = useRef<number | ReturnType<typeof setTimeout> | null>(null);

  // AI scanner state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);

  // Camera for photo capture
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  // Attach camera stream to video element once it mounts after setCameraActive(true)
  useEffect(() => {
    if (cameraActive && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraActive]);

  // ── No incident guard ───────────────────────────────────────

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598">
            <IconUserPlus size={30} />
          </ThemeIcon>
          <Title order={3} c="black">No Incident Selected</Title>
          <Text size="sm" c="dimmed" ta="center" maw={400}>
            Intake registration is scoped to an active incident. Pick one from the
            incident selector in the top bar, or visit the Incident Hub to create a new one.
          </Text>
          <Button mt="sm" onClick={() => navigate('/incidents')}>Go to Incident Hub</Button>
        </Stack>
      </Center>
    );
  }

  // ── Form helpers ────────────────────────────────────────────

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const resetAll = () => {
    setForm({ ...INITIAL_FORM, tagCode: generateTagCode() });
    setStep(0);
    setMethod(null);
    setFormModalOpen(false);
  };

  const applyParsedData = (data: Record<string, string>) => {
    setForm(prev => ({
      ...prev,
      firstName: data.firstName || prev.firstName,
      lastName: data.lastName || prev.lastName,
      middleName: data.middleName || prev.middleName,
      dob: data.dob || prev.dob,
      gender: data.sex || data.gender || prev.gender,
      address: data.address || prev.address,
      city: data.city || prev.city,
      state: data.state || prev.state,
      zip: data.zip || prev.zip,
    }));
  };

  // ── Photo capture ───────────────────────────────────────────

  const handlePhotoFile = (file: File | null) => {
    if (!file) return;
    updateForm('photo', file);
    updateForm('photoPreview', URL.createObjectURL(file));
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      cameraStreamRef.current = stream;
      setCameraActive(true);
    } catch {
      notifications.show({ title: 'Camera Error', message: 'Could not access camera.', color: 'var(--color-error)' });
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
        handlePhotoFile(file);
      }
    }, 'image/jpeg', 0.8);
    stopCamera();
  };

  const stopCamera = () => {
    cameraStreamRef.current?.getTracks().forEach(t => t.stop());
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  // ── Barcode scanner (native BarcodeDetector + zxing fallback) ──

  const handleScanResult = (raw: string) => {
    if (!raw || !scanActiveRef.current) return;
    stopBarcodeScan();
    // Parse AAMVA data from PDF417
    if (raw.startsWith('@') || raw.includes('ANSI ') || raw.includes('DL') || raw.includes('DAQ')) {
      const parsed = parseAAMVA(raw);
      if (parsed.lastName || parsed.firstName) {
        applyParsedData(parsed);
        notifications.show({ title: 'Scan Complete', message: 'ID data extracted successfully.', color: 'var(--color-success)' });
        setMethod('scan');
        setFormModalOpen(true);
        return;
      }
    }
    // Fallback: got a barcode but not AAMVA
    notifications.show({ title: 'Barcode Read', message: 'Barcode detected but not recognized as a driver\'s license. Try AI Scan instead.', color: '#C68700' });
  };

  const startBarcodeScan = async () => {
    setScanModalOpen(true);
    setScanning(false);
    setScanError('');
    scanActiveRef.current = true;

    // Rotate scanning tips
    const tips = [
      'Point at the PDF417 barcode on the back of the ID',
      'Hold the camera 4–8 inches from the barcode',
      'The barcode is the wide striped rectangle on the back',
      'Tilt the card slightly if glare is present',
      'Keep the camera very steady',
    ];
    let tipIdx = 0;
    const tipInterval = setInterval(() => {
      if (!scanActiveRef.current) { clearInterval(tipInterval); return; }
      tipIdx = (tipIdx + 1) % tips.length;
      setScanHint(tips[tipIdx]);
    }, 3000);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });

      if (!scanActiveRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      scanStreamRef.current = stream;

      const video = scanVideoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await new Promise<void>(res => { video.onloadedmetadata = () => res(); });
      await video.play();

      // Set canvas to match video resolution
      const canvas = scanCanvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
      }

      setScanning(true);

      // Try native BarcodeDetector first (Chrome/Edge — much better for PDF417)
      if ('BarcodeDetector' in window) {
        try {
          const BD = (window as unknown as Record<string, unknown>).BarcodeDetector as {
            getSupportedFormats: () => Promise<string[]>;
            new(opts: { formats: string[] }): { detect: (source: HTMLCanvasElement) => Promise<Array<{ rawValue: string }>> };
          };
          const supported = await BD.getSupportedFormats();
          if (supported.includes('pdf417')) {
            const detector = new BD({ formats: ['pdf417'] });
            // Native scan loop
            const tick = async () => {
              if (!scanActiveRef.current || !canvas || !video) return;
              if (video.readyState === video.HAVE_ENOUGH_DATA) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                  try {
                    const results = await detector.detect(canvas);
                    if (results.length > 0) {
                      handleScanResult(results[0].rawValue);
                      return;
                    }
                  } catch { /* detection failed this frame, try again */ }
                }
              }
              scanRafRef.current = requestAnimationFrame(tick);
            };
            scanRafRef.current = requestAnimationFrame(tick);
            return;
          }
        } catch { /* native not available, fall through */ }
      }

      // Fallback: zxing with PDF417 hints, canvas-based loop
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const { DecodeHintType, BarcodeFormat, NotFoundException } = await import('@zxing/library');
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417]);
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints);

      const tick = () => {
        if (!scanActiveRef.current || !canvas || !video) return;
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            try {
              const result = (reader as unknown as { decodeFromCanvas: (c: HTMLCanvasElement) => { getText: () => string } | null }).decodeFromCanvas(canvas);
              if (result) {
                handleScanResult(result.getText());
                return;
              }
            } catch (e) {
              if (!(e instanceof NotFoundException)) { /* ignore not-found, expected */ }
            }
          }
        }
        scanRafRef.current = setTimeout(tick, 200);
      };
      tick();

    } catch (err) {
      if (!scanActiveRef.current) return;
      const e = err as Error & { name: string };
      setScanError(
        e.name === 'NotAllowedError' ? 'Camera permission denied. Allow camera access in browser settings.' :
        e.name === 'NotFoundError' ? 'No camera found. Use AI Scan or Manual Entry.' :
        e.name === 'NotReadableError' ? 'Camera in use by another app. Close it and try again.' :
        `Camera error: ${e.message}`
      );
      setScanning(false);
    }
  };

  const stopBarcodeScan = () => {
    scanActiveRef.current = false;
    if (scanRafRef.current != null) {
      cancelAnimationFrame(scanRafRef.current as number);
      clearTimeout(scanRafRef.current as ReturnType<typeof setTimeout>);
      scanRafRef.current = null;
    }
    if (scanStreamRef.current) {
      scanStreamRef.current.getTracks().forEach(t => t.stop());
      scanStreamRef.current = null;
    }
    setScanModalOpen(false);
    setScanning(false);
  };

  // ── AI ID scanner ───────────────────────────────────────────

  const handleAiScanFile = async (file: File | null) => {
    if (!file) return;
    setAiProcessing(true);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip data URL prefix to get raw base64
          const b64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(b64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Determine media type
      const mediaType = file.type || 'image/jpeg';

      // Call Claude API directly for vision (callClaude only supports string content)
      const resp = await fetch(`${WORKER_URL}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Origin': ORIGIN },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 800,
          messages: [{
            role: 'user',
            content: [
              {
                type: 'image',
                source: { type: 'base64', media_type: mediaType, data: base64 },
              },
              {
                type: 'text',
                text: `Extract identification data from this ID card/document image. Return ONLY valid JSON with these fields (use empty string if not found):
{
  "firstName": "",
  "lastName": "",
  "middleName": "",
  "dob": "YYYY-MM-DD format",
  "sex": "M or F or X",
  "address": "",
  "city": "",
  "state": "2-letter code",
  "zip": ""
}
Do not include any text outside the JSON object.`,
              },
            ],
          }],
        }),
      });

      if (!resp.ok) throw new Error(`AI scan failed: ${resp.status}`);

      const data = await resp.json();
      const text = data?.content?.[0]?.text ?? '';

      // Parse JSON from response
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as Record<string, string>;
        applyParsedData(parsed);
        notifications.show({ title: 'AI Scan Complete', message: 'ID fields extracted.', color: 'var(--color-success)' });
        setAiModalOpen(false);
        setMethod('ai');
        setFormModalOpen(true);
      } else {
        throw new Error('Could not parse AI response');
      }
    } catch (err) {
      notifications.show({
        title: 'AI Scan Error',
        message: err instanceof Error ? err.message : 'Failed to process ID image.',
        color: 'var(--color-error)',
      });
    } finally {
      setAiProcessing(false);
    }
  };

  // ── Submit ──────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName) {
      notifications.show({ title: 'Required', message: 'First and last name are required.', color: 'var(--color-error)' });
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl = '';
      if (form.photo) {
        photoUrl = await resizeImageToBase64(form.photo, 800);
      }

      await createIndividual({
        incidentId: String(activeIncident.id),
        tagCode: form.tagCode,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        dob: form.dob || '',
        gender: form.gender || '',
        triage: form.triage || 'var(--color-success)',
        status: 'active',
        intakeMethod: method || 'manual',
        checkedInAt: new Date().toISOString(),
        medicalNeeds: form.medicalNeeds || '',
        medications: form.medications || '',
        emergencyContact: form.emergencyContact || '',
        contactPhone: form.contactPhone || '',
        guardianId: form.guardianId || '',
        photoUrl,
        notes: form.notes || '',
        // Phase 6 — Directory FK (only written when field is provisioned)
        ...(FIELDS.individuals.personId && linkedPersonId ? { personId: String(linkedPersonId) } : {}),
      });

      // Show success modal with print badge option
      setSuccessData({
        name: `${form.firstName} ${form.lastName}`,
        tagCode: form.tagCode,
        individual: {
          id: 0, tagCode: form.tagCode,
          firstName: form.firstName, lastName: form.lastName,
          dob: form.dob || '', gender: form.gender || '',
          triage: form.triage || 'var(--color-success)', status: 'active',
        } as Partial<Individual>,
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to register individual.',
        color: 'var(--color-error)',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = (s: number) => {
    if (s === 0) return !!(form.firstName && form.lastName);
    return true;
  };

  const age = calcAge(form.dob);
  const regenerateTag = () => updateForm('tagCode', generateTagCode());

  // Active individuals for guardian linking
  const activeIndividuals = individuals.filter(
    i => String(i.incidentId) === String(activeIncident.id) && i.status !== 'discharged'
  );

  // ══════════════════════════════════════════════════════════════
  // Render — Method selection cards (when no mode active)
  // ══════════════════════════════════════════════════════════════

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate('/intake')} size="sm">
            Back
          </Button>
          <Title order={3} c="black">New Registration</Title>
        </Group>
        <Text size="sm" c="dimmed">Incident: {activeIncident.name}</Text>
      </Group>

      {/* ── Method Selection Cards ──────────────────────────── */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg" mb="xl">
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{ cursor: 'pointer', textAlign: 'center', transition: 'box-shadow 150ms' }}
          onClick={() => { startBarcodeScan(); }}
          shadow="xs"
        >
          <ThemeIcon size={56} radius="xl" variant="light" color="var(--color-primary-700)" mx="auto" mb="md">
            <IconScan size={28} />
          </ThemeIcon>
          <Text fw={600} size="md" mb={4}>Scan Driver's License</Text>
          <Text size="xs" c="dimmed">Use camera to scan the PDF417 barcode on the back of a license</Text>
        </Paper>

        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{ cursor: 'pointer', textAlign: 'center', transition: 'box-shadow 150ms' }}
          onClick={() => setAiModalOpen(true)}
          shadow="xs"
        >
          <ThemeIcon size={56} radius="xl" variant="light" color="var(--color-primary-700)" mx="auto" mb="md">
            <IconRobot size={28} />
          </ThemeIcon>
          <Text fw={600} size="md" mb={4}>AI ID Scanner</Text>
          <Text size="xs" c="dimmed">Take a photo of any ID and let AI extract the fields automatically</Text>
        </Paper>

        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{ cursor: 'pointer', textAlign: 'center', transition: 'box-shadow 150ms' }}
          onClick={() => { setMethod('manual'); setFormModalOpen(true); }}
          shadow="xs"
        >
          <ThemeIcon size={56} radius="xl" variant="light" color="var(--color-success)" mx="auto" mb="md">
            <IconEdit size={28} />
          </ThemeIcon>
          <Text fw={600} size="md" mb={4}>Manual Entry</Text>
          <Text size="xs" c="dimmed">Type in all information by hand for walk-in registration</Text>
        </Paper>
      </SimpleGrid>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Barcode Scanner Modal                                 */}
      {/* ══════════════════════════════════════════════════════ */}
      <Modal
        opened={scanModalOpen}
        onClose={stopBarcodeScan}
        title="Scan Driver's License Barcode"
        size="lg"
        centered
      >
        <Stack align="center" gap="md">
          {scanError ? (
            <Paper p="lg" withBorder radius="sm" style={{ textAlign: 'center' }}>
              <ThemeIcon size={48} radius="xl" variant="light" color="var(--color-error)" mx="auto" mb="md">
                <IconX size={24} />
              </ThemeIcon>
              <Text size="sm" c="var(--color-error)">{scanError}</Text>
            </Paper>
          ) : (
            <div style={{ position: 'relative', width: '100%', maxWidth: 500, aspectRatio: '4/3', background: '#000', borderRadius: 4, overflow: 'hidden' }}>
              <video
                ref={scanVideoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <canvas ref={scanCanvasRef} style={{ display: 'none' }} />
              {/* Scanning frame overlay */}
              {scanning && (
                <div style={{
                  position: 'absolute', top: '20%', left: '10%', right: '10%', bottom: '20%',
                  border: '2px solid rgba(75,63,255,0.7)', borderRadius: 4,
                }}>
                  {/* Animated scan line */}
                  <div style={{
                    position: 'absolute', left: 0, right: 0, height: 2,
                    background: 'linear-gradient(90deg, transparent, var(--color-primary-700), transparent)',
                    animation: 'scanline 2s ease-in-out infinite',
                    top: '50%',
                  }} />
                </div>
              )}
              {!scanning && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Loader color="white" size="lg" />
                </div>
              )}
            </div>
          )}
          <Text size="sm" c="dimmed" ta="center">{scanHint}</Text>
          <Button variant="subtle" onClick={stopBarcodeScan} leftSection={<IconX size={14} />}>Cancel</Button>
        </Stack>
        <style>{`
          @keyframes scanline {
            0%, 100% { top: 20%; }
            50% { top: 80%; }
          }
        `}</style>
      </Modal>

      {/* ══════════════════════════════════════════════════════ */}
      {/* AI Scanner Modal                                      */}
      {/* ══════════════════════════════════════════════════════ */}
      <Modal
        opened={aiModalOpen}
        onClose={() => { if (!aiProcessing) setAiModalOpen(false); }}
        title="AI ID Scanner"
        size="md"
        centered
      >
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" variant="light" color="var(--data-viz-650)">
            <IconRobot size={24} />
          </ThemeIcon>
          <Text size="sm" ta="center">Take a photo or upload an image of any ID card. AI will extract the information automatically.</Text>

          {aiProcessing ? (
            <Stack align="center" gap="sm" py="lg">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">Analyzing ID with AI...</Text>
            </Stack>
          ) : (
            <Group justify="center">
              <FileButton onChange={handleAiScanFile} accept="image/*">
                {(props) => (
                  <Button leftSection={<IconPhoto size={14} />} variant="light" {...props}>
                    Upload Photo
                  </Button>
                )}
              </FileButton>
              <FileButton onChange={handleAiScanFile} accept="image/*" capture="environment" inputProps={{ capture: 'environment' } as Record<string, string>}>
                {(props) => (
                  <Button leftSection={<IconCamera size={14} />} {...props}>
                    Take Photo
                  </Button>
                )}
              </FileButton>
            </Group>
          )}

          <Button variant="subtle" onClick={() => setAiModalOpen(false)} disabled={aiProcessing}>Cancel</Button>
        </Stack>
      </Modal>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Registration Form Modal (3-step stepper)              */}
      {/* ══════════════════════════════════════════════════════ */}
      <Modal
        opened={formModalOpen}
        onClose={() => { if (!submitting) { setFormModalOpen(false); setMethod(null); } }}
        title={`Register Individual${method === 'scan' ? ' (Scanned)' : method === 'ai' ? ' (AI Scan)' : ''}`}
        size="xl"
        centered
        closeOnClickOutside={false}
      >
        <Stepper active={step} onStepClick={setStep} size="sm" mb="lg">
          {/* ── Step 1: Identity ──────────────────────────── */}
          <Stepper.Step label="Identity" description="Who">
            <Paper p="lg" withBorder radius="sm">
              <Stack gap="md">
                {/* Photo capture */}
                <Divider label="Photo (optional)" labelPosition="center" />
                {form.photoPreview ? (
                  <Group>
                    <Image src={form.photoPreview} w={120} h={120} radius="sm" fit="cover" />
                    <Button variant="light" color="var(--color-error)" size="xs" leftSection={<IconTrash size={14} />}
                      onClick={() => { updateForm('photo', null); updateForm('photoPreview', ''); }}>
                      Remove
                    </Button>
                  </Group>
                ) : cameraActive ? (
                  <Stack align="center">
                    <video ref={videoRef} autoPlay playsInline muted style={{ width: 300, borderRadius: 4 }} />
                    <Group>
                      <Button onClick={capturePhoto} leftSection={<IconCamera size={14} />}>Capture</Button>
                      <Button variant="subtle" onClick={stopCamera}>Cancel</Button>
                    </Group>
                  </Stack>
                ) : (
                  <Group>
                    <FileButton onChange={handlePhotoFile} accept="image/*">
                      {(props) => <Button variant="light" leftSection={<IconPhoto size={14} />} {...props}>Upload Photo</Button>}
                    </FileButton>
                    <Button variant="light" leftSection={<IconCamera size={14} />} onClick={startCamera}>
                      Use Camera
                    </Button>
                  </Group>
                )}

                {TABLES.people && (
                  <>
                    <Divider label="Directory Lookup (optional)" labelPosition="center" />
                    <div style={{ position: 'relative' }}>
                      <TextInput
                        placeholder="Search People Directory to link this intake..."
                        value={dirQuery}
                        onChange={e => handleDirQuery(e.target.value)}
                        size="sm"
                        rightSection={
                          linkedPersonId
                            ? <Badge size="xs" color="var(--color-success)" variant="filled" style={{ cursor: 'pointer' }} onClick={() => { setLinkedPersonId(null); setDirQuery(''); }}>Linked ×</Badge>
                            : dirSearching ? <span style={{ fontSize: 12, color: '#888' }}>…</span> : null
                        }
                      />
                      {dirResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', zIndex: 100, marginTop: 2 }}>
                          {dirResults.map(p => (
                            <button key={p.record_id} onClick={() => selectDirPerson(p)}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14 }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                            >
                              <span style={{ fontWeight: 500 }}>{p.firstName} {p.lastName}</span>
                              {p.phone && <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>{formatPhone(p.phone)}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <Divider label="Name" labelPosition="center" />
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <TextInput label="First Name *" placeholder="First name" value={form.firstName}
                    onChange={e => updateForm('firstName', e.target.value)} />
                  <TextInput label="Middle Name" placeholder="Middle" value={form.middleName}
                    onChange={e => updateForm('middleName', e.target.value)} />
                  <TextInput label="Last Name *" placeholder="Last name" value={form.lastName}
                    onChange={e => updateForm('lastName', e.target.value)} />
                </SimpleGrid>

                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <TextInput label="Date of Birth" type="date" value={form.dob}
                    onChange={e => updateForm('dob', e.target.value)}
                    description={age !== null ? `Age: ${age}` : undefined} />
                  <Select label="Sex" placeholder="Select" data={GENDER_OPTIONS}
                    value={form.gender || null} onChange={v => updateForm('gender', v || '')} clearable />
                  <Select label="ID Type" placeholder="Select" data={ID_TYPE_OPTIONS}
                    value={form.idType || null} onChange={v => updateForm('idType', v || '')} clearable />
                </SimpleGrid>

                <Divider label="Address" labelPosition="center" />
                <TextInput label="Street Address" value={form.address}
                  onChange={e => updateForm('address', e.target.value)} />
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <TextInput label="City" value={form.city}
                    onChange={e => updateForm('city', e.target.value)} />
                  <TextInput label="State" maxLength={2} value={form.state}
                    onChange={e => updateForm('state', e.target.value.toUpperCase())} />
                  <TextInput label="ZIP" maxLength={10} value={form.zip}
                    onChange={e => updateForm('zip', e.target.value)} />
                </SimpleGrid>

                <Divider label="Contact" labelPosition="center" />
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="Phone" placeholder="(555) 555-1234" value={form.phone}
                    onChange={e => updateForm('phone', formatPhone(e.target.value))} />
                  <TextInput label="Email" placeholder="email@example.com" value={form.email}
                    onChange={e => updateForm('email', e.target.value)} />
                </SimpleGrid>

                <Divider label="Minor Status" labelPosition="center" />
                <Checkbox
                  label="This individual is a minor (under 18)"
                  checked={form.isMinor}
                  onChange={e => updateForm('isMinor', e.currentTarget.checked)}
                />
                {form.isMinor && (
                  <Select
                    label="Link to Guardian"
                    placeholder="Select guardian (must already be registered)"
                    data={activeIndividuals.map(i => ({
                      value: String(i.id),
                      label: `${i.firstName} ${i.lastName} (${i.tagCode})`,
                    }))}
                    value={form.guardianId || null}
                    onChange={v => updateForm('guardianId', v || '')}
                    clearable
                    searchable
                  />
                )}
              </Stack>
            </Paper>
          </Stepper.Step>

          {/* ── Step 2: Triage ────────────────────────────── */}
          <Stepper.Step label="Triage" description="Assessment">
            <Paper p="lg" withBorder radius="sm">
              <Stack gap="md">
                <Text fw={500} size="sm">Triage Level</Text>
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  {[
                    { value: 'var(--color-success)', label: 'GREEN', desc: 'Walking Wounded / Minor', color: '#16a34a' },
                    { value: '#C68700', label: 'YELLOW', desc: 'Delayed Care', color: '#d97706' },
                    { value: 'var(--color-error)', label: 'RED', desc: 'Immediate / Critical', color: '#B12525' },
                  ].map(t => (
                    <Paper
                      key={t.value}
                      p="md"
                      radius="sm"
                      withBorder
                      style={{
                        textAlign: 'center', cursor: 'pointer',
                        borderColor: form.triage === t.value ? t.color : undefined,
                        borderWidth: form.triage === t.value ? 2 : 1,
                        background: form.triage === t.value ? `${t.color}10` : undefined,
                      }}
                      onClick={() => updateForm('triage', t.value)}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: t.color, margin: '0 auto 8px',
                      }} />
                      <Text fw={700} size="sm">{t.label}</Text>
                      <Text size="xs" c="dimmed">{t.desc}</Text>
                    </Paper>
                  ))}
                </SimpleGrid>

                <Divider label="Medical Information" labelPosition="center" />

                <Textarea label="Medical Needs" placeholder="Known medical conditions, allergies..."
                  value={form.medicalNeeds} onChange={e => updateForm('medicalNeeds', e.target.value)}
                  minRows={2} autosize />
                <TextInput label="Current Medications" placeholder="List current medications"
                  value={form.medications} onChange={e => updateForm('medications', e.target.value)} />

                <Divider label="Emergency Contact" labelPosition="center" />

                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="Emergency Contact Name" value={form.emergencyContact}
                    onChange={e => updateForm('emergencyContact', e.target.value)} />
                  <TextInput label="Contact Phone" value={form.contactPhone}
                    onChange={e => updateForm('contactPhone', formatPhone(e.target.value))} />
                </SimpleGrid>

                <Textarea label="Notes" placeholder="Additional notes..."
                  value={form.notes} onChange={e => updateForm('notes', e.target.value)}
                  minRows={2} autosize />
              </Stack>
            </Paper>
          </Stepper.Step>

          {/* ── Step 3: Confirm ───────────────────────────── */}
          <Stepper.Completed>
            <Paper p="lg" withBorder radius="sm">
              <Stack gap="md">
                <Title order={4}>Confirm Registration</Title>

                {/* Avatar + Tag Code */}
                <Group align="flex-start" gap="lg">
                  {form.photoPreview && (
                    <Image src={form.photoPreview} w={100} h={100} radius="md" fit="cover" />
                  )}
                  <Paper p="md" radius="sm" withBorder style={{ flex: 1, background: 'rgba(75,63,255,0.1)', textAlign: 'center' }}>
                    <Text size="xs" c="dimmed" mb={4}>TAG CODE</Text>
                    <Text fw={700} fz={32} ff="monospace">{form.tagCode}</Text>
                    <Group justify="center" gap="xs" mt="xs">
                      <CopyButton value={form.tagCode}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied!' : 'Copy'}>
                            <ActionIcon variant="light" size="sm" onClick={copy}>
                              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      <Tooltip label="Regenerate tag code">
                        <ActionIcon variant="light" size="sm" onClick={regenerateTag}>
                          <IconRefresh size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Paper>
                </Group>

                {/* Field summary */}
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <div>
                    <Text size="xs" c="dimmed">Name</Text>
                    <Text size="sm" fw={500}>
                      {form.firstName} {form.middleName ? `${form.middleName} ` : ''}{form.lastName}
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">DOB / Age</Text>
                    <Text size="sm">{form.dob || 'Not provided'}{age !== null ? ` (Age ${age})` : ''}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Triage</Text>
                    <Badge color={form.triage === 'var(--color-error)' ? 'var(--color-error)' : form.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}>
                      {form.triage.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Sex</Text>
                    <Text size="sm">{form.gender || 'Not specified'}</Text>
                  </div>
                  {form.address && (
                    <div>
                      <Text size="xs" c="dimmed">Address</Text>
                      <Text size="sm">{form.address}{form.city ? `, ${form.city}` : ''}{form.state ? `, ${form.state}` : ''} {form.zip}</Text>
                    </div>
                  )}
                  {form.phone && (
                    <div>
                      <Text size="xs" c="dimmed">Phone</Text>
                      <Text size="sm">{form.phone}</Text>
                    </div>
                  )}
                  {form.email && (
                    <div>
                      <Text size="xs" c="dimmed">Email</Text>
                      <Text size="sm">{form.email}</Text>
                    </div>
                  )}
                  {form.idType && (
                    <div>
                      <Text size="xs" c="dimmed">ID Type</Text>
                      <Text size="sm">{ID_TYPE_OPTIONS.find(o => o.value === form.idType)?.label || form.idType}</Text>
                    </div>
                  )}
                  {form.isMinor && (
                    <div>
                      <Text size="xs" c="dimmed">Minor</Text>
                      <Badge color="#F86A0B" variant="light">Minor</Badge>
                      {form.guardianId && (
                        <Text size="xs" mt={2}>
                          Guardian: {activeIndividuals.find(i => String(i.id) === form.guardianId)
                            ? `${activeIndividuals.find(i => String(i.id) === form.guardianId)!.firstName} ${activeIndividuals.find(i => String(i.id) === form.guardianId)!.lastName}`
                            : `ID ${form.guardianId}`}
                        </Text>
                      )}
                    </div>
                  )}
                  {form.emergencyContact && (
                    <div>
                      <Text size="xs" c="dimmed">Emergency Contact</Text>
                      <Text size="sm">{form.emergencyContact}{form.contactPhone ? ` \u2014 ${form.contactPhone}` : ''}</Text>
                    </div>
                  )}
                  {form.medicalNeeds && (
                    <div>
                      <Text size="xs" c="dimmed">Medical Needs</Text>
                      <Text size="sm">{form.medicalNeeds}</Text>
                    </div>
                  )}
                  {form.medications && (
                    <div>
                      <Text size="xs" c="dimmed">Medications</Text>
                      <Text size="sm">{form.medications}</Text>
                    </div>
                  )}
                  {form.notes && (
                    <div>
                      <Text size="xs" c="dimmed">Notes</Text>
                      <Text size="sm">{form.notes}</Text>
                    </div>
                  )}
                  <div>
                    <Text size="xs" c="dimmed">Intake Method</Text>
                    <Text size="sm">{method === 'scan' ? 'Barcode Scan' : method === 'ai' ? 'AI Scanner' : 'Manual Entry'}</Text>
                  </div>
                </SimpleGrid>

                <Divider />

                <Group justify="flex-end">
                  <Button variant="subtle" onClick={() => setStep(0)}>Edit</Button>
                  <Button leftSection={<IconCheck size={14} />} onClick={handleSubmit} loading={submitting} size="md">
                    Register Individual
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stepper.Completed>
        </Stepper>

        {step < 2 && (
          <Group justify="space-between" mt="md">
            <Button variant="subtle" leftSection={<IconArrowLeft size={14} />}
              onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
              Previous
            </Button>
            <Button rightSection={<IconArrowRight size={14} />}
              onClick={() => setStep(step + 1)} disabled={!canProceed(step)}>
              {step === 1 ? 'Review' : 'Next'}
            </Button>
          </Group>
        )}
      </Modal>

      {/* Success Modal — shows after registration with print badge option */}
      <Modal
        opened={!!successData}
        onClose={() => { setSuccessData(null); resetAll(); }}
        title={<Group gap="xs"><ThemeIcon size="sm" color="var(--color-success)" radius="xl"><IconCheck size={14} /></ThemeIcon><Text fw={600}>Registration Complete</Text></Group>}
        centered size="sm"
      >
        {successData && (
          <Stack gap="md" align="center">
            <Paper p="lg" radius="md" withBorder style={{ backgroundColor: '#F0FDF4', textAlign: 'center', width: '100%' }}>
              <Text size="sm" c="dimmed" mb={4}>Registration Number</Text>
              <Text fw={700} ff="monospace" style={{ color: '#059669', letterSpacing: 2, fontSize: 32 }}>
                {successData.tagCode}
              </Text>
              <Divider my="sm" />
              <Text size="lg" fw={600}>{successData.name}</Text>
            </Paper>

            <Group gap="sm" justify="center">
              <CopyButton value={successData.tagCode}>
                {({ copied, copy }) => (
                  <Button size="sm" variant="light" color={copied ? 'var(--color-success)' : 'var(--color-primary-700)'} leftSection={<IconCopy size={14} />} onClick={copy}>
                    {copied ? 'Copied' : 'Copy Tag'}
                  </Button>
                )}
              </CopyButton>
              <Button
                size="sm"
                color="#112D4E"
                leftSection={<IconPrinter size={14} />}
                onClick={async () => {
                  await printBadge(
                    successData.individual as Individual,
                    activeIncident?.name || '',
                  );
                  notifications.show({ title: 'Badge PDF', message: 'Badge PDF generated.', color: 'var(--color-primary-700)' });
                }}
              >
                Print Badge
              </Button>
            </Group>

            <Button fullWidth variant="subtle" color="#939598" onClick={() => { setSuccessData(null); resetAll(); }}>
              Register Another
            </Button>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
